import { useEffect, useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../auth/context/AuthContext';
import { apiFetch } from '../../../shared/lib/apiClient';
import quickbooksLogo from '../../../shared/assets/logos/integrations/quickbooks.svg';

const PROVIDER_META = {
  quickbooks: { name: 'QuickBooks', logo: quickbooksLogo },
};
const SYNCABLE_PROVIDERS = new Set(['quickbooks']);

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_NOTIFICATION_EMAILS = 7;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const STATUS_META = {
  scheduled:      { label: 'Scheduled', dot: 'bg-sky-400',     text: 'text-sky-700',     bg: 'bg-sky-50',     border: 'border-sky-100'     },
  overdue:        { label: 'Overdue',   dot: 'bg-red-500',     text: 'text-red-700',     bg: 'bg-red-50',     border: 'border-red-100'     },
  partially_paid: { label: 'Partial',   dot: 'bg-amber-400',   text: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-100'   },
  fully_paid:     { label: 'Paid',      dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-100' },
};

const FLAG_OPTIONS = [
  { value: 'none',          label: 'No flag'      },
  { value: 'follow_up',     label: 'Follow up'    },
  { value: 'cash_risk',     label: 'Cash risk'    },
  { value: 'vendor_called', label: 'Called vendor' },
  { value: 'dispute',       label: 'Dispute'      },
];

const CLUSTER_SINGLE = 12000;
const CLUSTER_DAY = 32000;
const CLUSTER_WEEK = 90000;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtCompact = (n) => {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${Math.round(n)}`;
};

const toDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const parseDateKey = (dk) => {
  if (!dk) return null;
  const [y, m, d] = dk.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const addMonths = (date, n) => new Date(date.getFullYear(), date.getMonth() + n, 1);
const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);
const startOfWeek = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return d;
};
const endOfWeek = (date) => {
  const s = startOfWeek(date);
  s.setDate(s.getDate() + 6);
  return s;
};
const addDays = (date, days) => {
  const n = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  n.setDate(n.getDate() + days);
  return n;
};
const todayKey = () => toDateKey(new Date());
const sum = (items, fn) => items.reduce((a, x) => a + fn(x), 0);
const pctDelta = (cur, prev) => prev === 0 ? (cur === 0 ? 0 : 100) : ((cur - prev) / prev) * 100;

const fmtDateLong = (dk) => {
  const d = parseDateKey(dk);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};

const fmtDateShort = (dk) => {
  const d = parseDateKey(dk);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const CalendarPro = ({ loaded }) => {
  const { companies, activeCompany, switchCompany } = useAuth();

  const companyOptions = useMemo(() =>
    companies.map((e) => ({ id: e.companyId, name: e.company?.name || 'Unnamed company' })),
    [companies]
  );

  // Core state
  const [paymentsByCompany, setPaymentsByCompany] = useState({});
  const [localCompanyId, setLocalCompanyId] = useState(companyOptions[0]?.id || null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const [selectedDateKey, setSelectedDateKey] = useState(todayKey());
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  // Detail
  const [detailNoteDraft, setDetailNoteDraft] = useState('');
  const [detailFlagDraft, setDetailFlagDraft] = useState('none');
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [detailSaveMessage, setDetailSaveMessage] = useState('');

  // Notifications
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationEmails, setNotificationEmails] = useState([]);
  const [notificationEmailDraft, setNotificationEmailDraft] = useState('');
  const [notificationError, setNotificationError] = useState('');
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);
  const [notificationFrequency, setNotificationFrequency] = useState('weekly');
  const [notificationLeadDays, setNotificationLeadDays] = useState([3, 7]);
  const [notifyOverdue, setNotifyOverdue] = useState(true);
  const [notificationModalStep, setNotificationModalStep] = useState(1);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showPaid, setShowPaid] = useState(false);
  const [partyFilter, setPartyFilter] = useState('all');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');

  // Integration sync
  const [syncStatus, setSyncStatus] = useState(null); // null | { provider, status: 'syncing'|'done'|'error', name, logo }
  const [activeIntegrations, setActiveIntegrations] = useState([]);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // Company
  useEffect(() => {
    if (!activeCompany?.companyId && !localCompanyId && companyOptions[0]?.id)
      setLocalCompanyId(companyOptions[0].id);
  }, [companyOptions, activeCompany, localCompanyId]);

  const selectedCompanyId = activeCompany?.companyId || localCompanyId || companyOptions[0]?.id || '';

  // Load payments
  const fetchPayments = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    setLoadError('');
    try {
      const res = await apiFetch(`/api/calendar/payments?${new URLSearchParams({ source: 'auto', limit: '1000', includeConflicts: 'true' })}`, { companyId: selectedCompanyId });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setPaymentsByCompany((p) => ({ ...p, [selectedCompanyId]: data.payments || [] }));
      setConflicts(data.conflicts || []);
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCompanyId]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  // Sync a single provider
  const syncProvider = useCallback(async (provider) => {
    const meta = PROVIDER_META[provider] || { name: provider, logo: null };
    setSyncStatus({ provider, status: 'syncing', name: meta.name, logo: meta.logo });
    try {
      const sr = await apiFetch(`/api/sync/${provider}`, { method: 'POST' });
      const d = sr.ok ? await sr.json() : null;
      setSyncStatus({ provider, status: 'done', name: meta.name, logo: meta.logo });
      setLastSyncTime(new Date());
      if (d?.bills?.synced > 0 || d?.payments?.synced > 0) fetchPayments();
      setTimeout(() => setSyncStatus(null), 3000);
    } catch {
      setSyncStatus({ provider, status: 'error', name: meta.name, logo: meta.logo });
      setTimeout(() => setSyncStatus(null), 4000);
    }
  }, [fetchPayments]);

  // Load active integrations + auto-sync (throttled)
  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        const res = await apiFetch('/api/integrations');
        if (!res.ok) return;
        const { integrations = [] } = await res.json();
        const syncable = integrations.filter(i => i.status === 'active' && SYNCABLE_PROVIDERS.has(i.provider));
        if (!cancelled) setActiveIntegrations(syncable);

        for (const int of syncable) {
          if (cancelled) break;
          const key = `arbcor_calpro_sync_${int.provider}`;
          const last = parseInt(sessionStorage.getItem(key) || '0', 10);
          if (Date.now() - last < 5 * 60 * 1000) continue;
          sessionStorage.setItem(key, String(Date.now()));
          await syncProvider(int.provider);
        }
      } catch { /* skip */ }
    }
    init();
    return () => { cancelled = true; };
  }, [syncProvider]);

  // Load notification settings
  useEffect(() => {
    if (!selectedCompanyId) return;
    let cancelled = false;
    apiFetch('/api/calendar/settings', { companyId: selectedCompanyId })
      .then(r => r.json().catch(() => ({})))
      .then(data => {
        if (cancelled) return;
        const s = data.settings || {};
        setNotificationsEnabled(!!s.notificationsEnabled);
        setNotificationEmails(Array.isArray(s.notificationEmails) ? s.notificationEmails : []);
        if (s.notificationFrequency) setNotificationFrequency(s.notificationFrequency);
        if (Array.isArray(s.notificationLeadDays)) setNotificationLeadDays(s.notificationLeadDays);
        if (typeof s.notifyOverdue === 'boolean') setNotifyOverdue(s.notifyOverdue);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedCompanyId]);

  // Derived data
  const allPayments = useMemo(() => paymentsByCompany[selectedCompanyId] || [], [paymentsByCompany, selectedCompanyId]);
  const today = todayKey();

  const uniquePartyOptions = useMemo(() =>
    [...new Set(allPayments.map(p => p.partyName))].sort((a, b) => a.localeCompare(b)),
    [allPayments]
  );

  const parsedMinAmount = minAmount === '' ? null : Number(minAmount);
  const parsedMaxAmount = maxAmount === '' ? null : Number(maxAmount);

  // Apply filters
  const payments = useMemo(() => {
    let list = allPayments;
    if (!showPaid) list = list.filter(p => p.status !== 'fully_paid');
    if (statusFilter !== 'all') list = list.filter(p => p.status === statusFilter);
    if (partyFilter !== 'all') list = list.filter(p => p.partyName === partyFilter);
    if (parsedMinAmount !== null) list = list.filter(p => p.amount >= parsedMinAmount);
    if (parsedMaxAmount !== null) list = list.filter(p => p.amount <= parsedMaxAmount);
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      list = list.filter(p =>
        (p.partyName || '').toLowerCase().includes(q) ||
        (p.invoiceNumber || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [allPayments, showPaid, statusFilter, searchTerm, partyFilter, parsedMinAmount, parsedMaxAmount]);

  const selectedPayment = useMemo(() => allPayments.find((p) => p.id === selectedPaymentId) || null, [allPayments, selectedPaymentId]);
  const selectedSavedNote = selectedPayment ? (selectedPayment.quickNote ?? selectedPayment.notes ?? '') : '';
  const selectedSavedFlag = selectedPayment ? (selectedPayment.flag ?? 'none') : 'none';
  const hasUnsavedDetailChanges = Boolean(selectedPayment && (detailNoteDraft !== selectedSavedNote || detailFlagDraft !== selectedSavedFlag));

  useEffect(() => {
    if (!selectedPayment) { setDetailNoteDraft(''); setDetailFlagDraft('none'); setDetailSaveMessage(''); return; }
    setDetailNoteDraft(selectedSavedNote);
    setDetailFlagDraft(selectedSavedFlag);
    setDetailSaveMessage('');
  }, [selectedPayment, selectedSavedNote, selectedSavedFlag]);

  // Group payments by date for calendar
  const paymentsByDate = useMemo(() => {
    const map = {};
    payments.forEach(p => {
      if (!map[p.dueDate]) map[p.dueDate] = [];
      map[p.dueDate].push(p);
    });
    Object.values(map).forEach(arr =>
      arr.sort((a, b) => {
        if (a.status === 'overdue' && b.status !== 'overdue') return -1;
        if (b.status === 'overdue' && a.status !== 'overdue') return 1;
        return b.amount - a.amount;
      })
    );
    return map;
  }, [payments]);

  // Stats (from ours)
  const stats = useMemo(() => {
    const ws = toDateKey(startOfWeek(new Date()));
    const we = toDateKey(endOfWeek(new Date()));
    const open = allPayments.filter(p => p.status !== 'fully_paid');
    const thisWeek = open.filter(p => p.dueDate >= ws && p.dueDate <= we);
    const overdue = open.filter(p => p.status === 'overdue');
    const ms = toDateKey(startOfMonth(new Date()));
    const me = toDateKey(endOfMonth(new Date()));
    const thisMonth = open.filter(p => p.dueDate >= ms && p.dueDate <= me);

    // Trend: previous month
    const prevMs = toDateKey(startOfMonth(addMonths(new Date(), -1)));
    const prevMe = toDateKey(endOfMonth(addMonths(new Date(), -1)));
    const lastMonth = open.filter(p => p.dueDate >= prevMs && p.dueDate <= prevMe);
    const monthDelta = pctDelta(
      sum(thisMonth, p => p.outstandingAmount),
      sum(lastMonth, p => p.outstandingAmount)
    );

    return {
      weekCount: thisWeek.length,
      weekAmount: sum(thisWeek, p => p.outstandingAmount),
      overdueCount: overdue.length,
      overdueAmount: sum(overdue, p => p.outstandingAmount),
      monthCount: thisMonth.length,
      monthAmount: sum(thisMonth, p => p.outstandingAmount),
      monthDelta,
    };
  }, [allPayments]);

  // Cluster alerts (from HK)
  const clusterAlerts = useMemo(() => {
    const daily = Object.entries(paymentsByDate)
      .map(([dk, records]) => ({
        dateKey: dk,
        total: sum(records, p => p.amount),
        count: records.length,
        largeCount: records.filter(p => p.amount >= CLUSTER_SINGLE).length,
      }))
      .filter(e => e.total >= CLUSTER_DAY || e.largeCount >= 2)
      .sort((a, b) => b.total - a.total)
      .slice(0, 3);

    const weeklyMap = {};
    payments.forEach(p => {
      const wk = toDateKey(startOfWeek(parseDateKey(p.dueDate) || new Date()));
      if (!weeklyMap[wk]) weeklyMap[wk] = { weekKey: wk, count: 0, total: 0, largeCount: 0 };
      weeklyMap[wk].count++;
      weeklyMap[wk].total += p.amount;
      if (p.amount >= CLUSTER_SINGLE) weeklyMap[wk].largeCount++;
    });

    const weekly = Object.values(weeklyMap)
      .filter(e => e.total >= CLUSTER_WEEK || e.largeCount >= 4)
      .sort((a, b) => b.total - a.total)
      .slice(0, 2);

    return { daily, weekly };
  }, [paymentsByDate, payments]);

  // Calendar grid cells (from ours)
  const calendarCells = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(currentMonth));
    const gridEnd = endOfWeek(endOfMonth(currentMonth));
    const cells = [];
    for (let d = new Date(gridStart); d <= gridEnd; d = addDays(d, 1)) {
      cells.push(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
    }
    return cells;
  }, [currentMonth]);

  const selectedDatePayments = useMemo(() => paymentsByDate[selectedDateKey] || [], [paymentsByDate, selectedDateKey]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleRefresh = async () => {
    if (!selectedCompanyId) return;
    setIsRefreshing(true);
    setLoadError('');
    try {
      const res = await apiFetch('/api/calendar/refresh', { method: 'POST', body: JSON.stringify({ source: 'auto' }), companyId: selectedCompanyId });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Refresh failed');
      setPaymentsByCompany((p) => ({ ...p, [selectedCompanyId]: data.payments || [] }));
      setConflicts(data.conflicts || []);
    } catch (e) { setLoadError(e.message); }
    finally { setIsRefreshing(false); }
  };

  const handleSaveDetails = async () => {
    if (!selectedPayment || !hasUnsavedDetailChanges || isSavingDetails) return;
    setIsSavingDetails(true);
    setDetailSaveMessage('');
    try {
      const res = await apiFetch(`/api/calendar/payments/${encodeURIComponent(selectedPayment.id)}/details`, {
        method: 'PUT', body: JSON.stringify({ note: detailNoteDraft, flag: detailFlagDraft }), companyId: selectedCompanyId,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setPaymentsByCompany((prev) => ({
        ...prev,
        [selectedCompanyId]: (prev[selectedCompanyId] || []).map((p) =>
          p.id === selectedPayment.id ? { ...p, notes: detailNoteDraft, quickNote: detailNoteDraft, flag: detailFlagDraft } : p
        ),
      }));
      if (Array.isArray(data.conflicts)) setConflicts(data.conflicts);
      setDetailSaveMessage('Saved');
    } catch (e) { setLoadError(e.message); }
    finally { setIsSavingDetails(false); }
  };

  const handleAddNotificationEmail = () => {
    const next = notificationEmailDraft.trim().toLowerCase();
    if (!next) return;
    if (notificationEmails.length >= MAX_NOTIFICATION_EMAILS) { setNotificationError(`Max ${MAX_NOTIFICATION_EMAILS} emails.`); return; }
    if (!EMAIL_PATTERN.test(next)) { setNotificationError('Enter a valid email.'); return; }
    if (notificationEmails.includes(next)) { setNotificationError('Already added.'); return; }
    setNotificationEmails((p) => [...p, next]);
    setNotificationEmailDraft('');
    setNotificationError('');
  };

  const persistNotificationSettings = async (payload) => {
    if (!selectedCompanyId) return false;
    setIsSavingNotifications(true);
    setNotificationError('');
    try {
      const res = await apiFetch('/api/calendar/settings', { method: 'PUT', body: JSON.stringify(payload), companyId: selectedCompanyId });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      const s = data.settings || {};
      setNotificationsEnabled(!!s.notificationsEnabled);
      setNotificationEmails(Array.isArray(s.notificationEmails) ? s.notificationEmails : []);
      if (s.notificationFrequency) setNotificationFrequency(s.notificationFrequency);
      if (Array.isArray(s.notificationLeadDays)) setNotificationLeadDays(s.notificationLeadDays);
      if (typeof s.notifyOverdue === 'boolean') setNotifyOverdue(s.notifyOverdue);
      return true;
    } catch (e) { setNotificationError(e.message); return false; }
    finally { setIsSavingNotifications(false); }
  };

  const handleNotificationToggle = async () => {
    if (isSavingNotifications) return;
    if (notificationsEnabled) { await persistNotificationSettings({ notificationsEnabled: false }); return; }
    setNotificationError('');
    setNotificationModalStep(1);
    setIsNotificationModalOpen(true);
  };

  const handleSaveNotifications = async () => {
    if (notificationEmails.length === 0) { setNotificationError('Add at least one email.'); return; }
    const saved = await persistNotificationSettings({
      notificationsEnabled: true,
      notificationEmails,
      notificationFrequency,
      notificationLeadDays,
      notifyOverdue,
    });
    if (saved) setIsNotificationModalOpen(false);
  };

  const toggleLeadDay = (day) => {
    setNotificationLeadDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => a - b)
    );
  };

  const goToToday = () => {
    setCurrentMonth(startOfMonth(new Date()));
    setSelectedDateKey(today);
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || partyFilter !== 'all' || minAmount || maxAmount || showPaid;
  const resetFilters = () => { setSearchTerm(''); setStatusFilter('all'); setPartyFilter('all'); setMinAmount(''); setMaxAmount(''); setShowPaid(false); };
  const handleCompanyChange = (id) => { setLocalCompanyId(id); if (switchCompany) switchCompany(id); };

  const hasAlerts = clusterAlerts.daily.length > 0 || clusterAlerts.weekly.length > 0;
  const rightPanelContent = selectedPayment ? 'detail' : 'dayList';

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-4 max-w-[1400px] mx-auto">

      {/* Header row */}
      <div
        className="flex items-center justify-between transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '150ms' }}
      >
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">Payment Calendar</h1>
          <p className="text-sm text-zinc-400 mt-0.5">
            Know when every bill is due
            {allPayments.length > 0 && <span className="text-zinc-500"> · {allPayments.length} payments tracked</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleNotificationToggle}
            disabled={isSavingNotifications}
            className={`p-2 rounded-lg border transition-colors ${notificationsEnabled ? 'bg-zinc-900 border-zinc-900 text-white' : 'bg-white border-zinc-200 text-zinc-400 hover:border-zinc-400 hover:text-zinc-600'}`}
            title={notificationsEnabled ? 'Notifications on' : 'Notifications off'}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 6a4 4 0 0 1 8 0c0 4 2 5 2 5H2s2-1 2-5" /><path d="M6.5 13a1.5 1.5 0 0 0 3 0" />
            </svg>
          </button>
        </div>
      </div>

      {/* Integration sync bar */}
      {activeIntegrations.length > 0 && (
        <div
          className="flex items-center gap-2 flex-wrap transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '220ms' }}
        >
          {activeIntegrations.map(int => {
            const meta = PROVIDER_META[int.provider] || { name: int.provider, logo: null };
            const isSyncing = syncStatus?.provider === int.provider && syncStatus.status === 'syncing';
            const isDone = syncStatus?.provider === int.provider && syncStatus.status === 'done';
            const isError = syncStatus?.provider === int.provider && syncStatus.status === 'error';
            const isIdle = !isSyncing && !isDone && !isError;
            return (
              <button
                key={int.provider}
                disabled={isSyncing}
                onClick={() => syncProvider(int.provider)}
                className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border text-sm font-medium transition-all duration-300 ${
                  isSyncing ? 'bg-zinc-50 border-zinc-200 text-zinc-500 cursor-wait' :
                  isDone    ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                  isError   ? 'bg-red-50 border-red-200 text-red-600' :
                  isIdle && lastSyncTime ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:border-emerald-300 cursor-pointer' :
                              'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400 hover:text-zinc-900 cursor-pointer'
                }`}
              >
                {meta.logo && <img src={meta.logo} alt={meta.name} className="w-4 h-4 shrink-0" />}
                {isSyncing && <div className="w-3.5 h-3.5 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin shrink-0" />}
                {(isDone || (isIdle && lastSyncTime)) && (
                  <svg className="w-3.5 h-3.5 text-emerald-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {isError && (
                  <svg className="w-3.5 h-3.5 text-red-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
                {isSyncing ? `Syncing ${meta.name}...` :
                 isDone    ? `${meta.name} synced` :
                 isError   ? `${meta.name} failed` :
                 isIdle && lastSyncTime ? <>{meta.name} synced <span className="text-emerald-500/60 font-normal">· {lastSyncTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span></> :
                             `Sync ${meta.name}`}
              </button>
            );
          })}
        </div>
      )}

      {loadError && <p className="text-xs text-red-500">{loadError}</p>}

      {/* Filter bar — clean, inline */}
      <div
        className="flex items-center gap-3 flex-wrap transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '250ms' }}
      >
        <div className="relative flex-1 min-w-[180px] max-w-[280px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="7" cy="7" r="5" /><path d="M11 11L14 14" />
          </svg>
          <input
            type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search vendors, invoices..."
            className="w-full pl-9 pr-3 py-2 bg-white border border-zinc-200 text-sm text-zinc-800 placeholder-zinc-400 rounded-lg outline-none focus:ring-1 focus:ring-zinc-900 transition-shadow"
          />
        </div>

        <select
          value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-900"
        >
          <option value="all">All statuses</option>
          <option value="scheduled">Scheduled</option>
          <option value="overdue">Overdue</option>
          <option value="partially_paid">Partial</option>
          <option value="fully_paid">Paid</option>
        </select>

        <select
          value={partyFilter} onChange={(e) => setPartyFilter(e.target.value)}
          className="px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-900"
        >
          <option value="all">All vendors</option>
          {uniquePartyOptions.map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>

        <button
          onClick={() => setShowFilters(f => !f)}
          className={`px-3 py-2 rounded-lg border text-sm transition-colors ${showFilters ? 'bg-zinc-900 border-zinc-900 text-white' : 'bg-white border-zinc-200 text-zinc-500 hover:border-zinc-400 hover:text-zinc-700'}`}
        >
          {showFilters ? 'Less' : 'More'}
        </button>

        {hasActiveFilters && (
          <button onClick={resetFilters}
            className="text-[12px] text-zinc-400 hover:text-zinc-700 transition-colors">
            Clear
          </button>
        )}

        <span className="text-[12px] text-zinc-400 ml-auto">{payments.length} payments</span>
      </div>

      {/* Expanded filters */}
      {showFilters && (
        <div
          className="flex items-center gap-3 flex-wrap"
          style={{ animation: 'fadeSlideDown 0.2s ease-out' }}
        >
          <label className="flex items-center gap-2 text-sm text-zinc-600">
            <span className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Min $</span>
            <input
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              placeholder="0"
              type="number"
              min="0"
              className="w-24 px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900"
            />
          </label>

          <label className="flex items-center gap-2 text-sm text-zinc-600">
            <span className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Max $</span>
            <input
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
              placeholder="50000"
              type="number"
              min="0"
              className="w-24 px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-900"
            />
          </label>

          {companyOptions.length > 1 && (
            <label className="flex items-center gap-2 text-sm text-zinc-600">
              <span className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Company</span>
              <select
                value={selectedCompanyId}
                onChange={(e) => handleCompanyChange(e.target.value)}
                className="px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-900"
              >
                {companyOptions.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>
          )}

          <label className="flex items-center gap-1.5 text-[12px] text-zinc-600 cursor-pointer ml-auto">
            <input type="checkbox" checked={showPaid} onChange={(e) => setShowPaid(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900" />
            Include paid
          </label>
        </div>
      )}

      {/* Summary cards */}
      <div
        className="grid grid-cols-3 gap-2 sm:gap-3 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '220ms' }}
      >
        <div className="bg-white border border-zinc-200 rounded-xl p-2.5 sm:p-4 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-medium truncate">Due This Week</p>
          <p className="text-sm sm:text-2xl font-light text-zinc-900 mt-1 tabular-nums truncate">{fmtCompact(stats.weekAmount)}</p>
          <p className="text-[11px] text-zinc-400 mt-0.5">{stats.weekCount} bill{stats.weekCount !== 1 ? 's' : ''}</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-xl p-2.5 sm:p-4 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-medium truncate">Due This Month</p>
          <p className="text-sm sm:text-2xl font-light text-zinc-900 mt-1 tabular-nums truncate">{fmtCompact(stats.monthAmount)}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[11px] text-zinc-400">{stats.monthCount} bill{stats.monthCount !== 1 ? 's' : ''}</span>
            {stats.monthDelta !== 0 && (
              <span className={`text-[10px] font-medium ${stats.monthDelta >= 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {stats.monthDelta >= 0 ? '+' : ''}{stats.monthDelta.toFixed(0)}% vs last
              </span>
            )}
          </div>
        </div>
        {stats.overdueCount > 0 ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-2.5 sm:p-4 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.15em] text-red-400 font-medium truncate">Overdue</p>
            <p className="text-sm sm:text-2xl font-light text-red-600 mt-1 tabular-nums truncate">{fmtCompact(stats.overdueAmount)}</p>
            <p className="text-[11px] text-red-400 mt-0.5">{stats.overdueCount} bill{stats.overdueCount !== 1 ? 's' : ''}</p>
          </div>
        ) : (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2.5 sm:p-4 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.15em] text-emerald-500 font-medium truncate">Overdue</p>
            <p className="text-sm sm:text-2xl font-light text-emerald-600 mt-1 tabular-nums">$0</p>
            <p className="text-[11px] text-emerald-400 mt-0.5">All clear</p>
          </div>
        )}
      </div>

      {/* Cluster alerts (from HK) */}
      {hasAlerts && (
        <div
          className="rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50/50 to-white px-4 py-3 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '280ms' }}
        >
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-[11px] font-medium text-amber-900">Cash-flow concentration alerts</p>
            <span className="text-[10px] text-amber-600 px-2 py-0.5 rounded-full border border-amber-200 bg-white/70">risk watch</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {clusterAlerts.daily.map((a) => (
              <button key={`d-${a.dateKey}`} onClick={() => { setSelectedDateKey(a.dateKey); setCurrentMonth(startOfMonth(parseDateKey(a.dateKey) || new Date())); }}
                className="rounded-lg border border-amber-200 bg-white/80 px-3 py-1.5 text-left hover:bg-white transition-colors">
                <p className="text-[11px] font-medium text-amber-900">{fmtDateShort(a.dateKey)}</p>
                <p className="text-[10px] text-amber-700">{a.count} payments · {fmtCompact(a.total)}</p>
              </button>
            ))}
            {clusterAlerts.weekly.map((a) => (
              <button key={`w-${a.weekKey}`} onClick={() => { setSelectedDateKey(a.weekKey); setCurrentMonth(startOfMonth(parseDateKey(a.weekKey) || new Date())); }}
                className="rounded-lg border border-amber-200 bg-white/80 px-3 py-1.5 text-left hover:bg-white transition-colors">
                <p className="text-[11px] font-medium text-amber-900">Week of {fmtDateShort(a.weekKey)}</p>
                <p className="text-[10px] text-amber-700">{a.count} payments · {fmtCompact(a.total)}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Calendar + Right Panel */}
      <div
        className="flex flex-col lg:flex-row gap-4 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(16px)', transitionDelay: '340ms' }}
      >

        {/* Calendar Grid */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}
                className="w-8 h-8 rounded-lg border border-zinc-200 bg-white flex items-center justify-center text-zinc-400 hover:text-zinc-800 hover:border-zinc-400 transition-colors">
                <svg width="14" height="14" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M8 2L4 6l4 4" /></svg>
              </button>
              <h2 className="text-lg font-semibold text-zinc-900 min-w-[180px] text-center">
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                className="w-8 h-8 rounded-lg border border-zinc-200 bg-white flex items-center justify-center text-zinc-400 hover:text-zinc-800 hover:border-zinc-400 transition-colors">
                <svg width="14" height="14" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M4 2l4 4-4 4" /></svg>
              </button>
            </div>
            <button onClick={goToToday}
              className="px-3 py-1.5 rounded-lg border border-zinc-200 text-[11px] uppercase tracking-widest text-zinc-500 hover:text-zinc-800 hover:border-zinc-400 transition-colors">
              Today
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-5 h-5 border-2 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
            </div>
          ) : (
            <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden">
              <div className="grid grid-cols-7 border-b border-zinc-200">
                {DAYS.map((d) => (
                  <div key={d} className="py-2.5 text-center text-[10px] uppercase tracking-widest text-zinc-400 font-medium">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7" style={{ gridAutoRows: `${Math.min(120, Math.max(88, Math.floor((window.innerHeight - 420) / (calendarCells.length / 7))))}px` }}>
                {calendarCells.map((cellDate) => {
                  const dk = toDateKey(cellDate);
                  const dayPayments = paymentsByDate[dk] || [];
                  const outside = cellDate.getMonth() !== currentMonth.getMonth();
                  const isToday = dk === today;
                  const isSelected = dk === selectedDateKey;
                  const isPast = dk < today;
                  const hasOverdue = dayPayments.some(p => p.status === 'overdue');
                  const dayTotal = dayPayments.reduce((s, p) => s + p.outstandingAmount, 0);
                  const isClustered = dayTotal >= CLUSTER_DAY || dayPayments.filter(p => p.amount >= CLUSTER_SINGLE).length >= 2;

                  return (
                    <button
                      key={dk}
                      onClick={() => { setSelectedDateKey(dk); setSelectedPaymentId(null); }}
                      className={`relative border-r border-b border-zinc-200 p-1.5 text-left transition-all duration-150 ${
                        outside ? 'bg-zinc-100/50' :
                        isSelected ? 'bg-blue-50/70 ring-1 ring-inset ring-blue-200' :
                        'bg-white hover:bg-zinc-50/60'
                      } ${hasOverdue && !outside ? 'shadow-[inset_0_2px_0_0_rgba(239,68,68,0.35)]' : ''}`}
                    >
                      <div className="flex items-center justify-between mb-0.5">
                        <span className={`text-[11px] font-semibold w-6 h-6 inline-flex items-center justify-center rounded-full transition-all ${
                          isToday ? 'bg-zinc-900 text-white' :
                          outside ? 'text-zinc-300' :
                          isPast && hasOverdue ? 'text-red-500' :
                          'text-zinc-600'
                        }`}>
                          {cellDate.getDate()}
                        </span>
                        {dayPayments.length > 0 && !outside && (
                          <span className={`text-[9px] tabular-nums font-semibold ${hasOverdue ? 'text-red-500' : 'text-zinc-400'}`}>
                            {fmtCompact(dayTotal)}
                          </span>
                        )}
                      </div>

                      {!outside && dayPayments.length > 0 && (
                        <div className="space-y-px">
                          {dayPayments.slice(0, 2).map((p) => {
                            const meta = STATUS_META[p.status] || STATUS_META.scheduled;
                            return (
                              <div key={p.id}
                                className={`flex items-center gap-1 px-1 py-0.5 rounded text-[9px] leading-tight truncate ${
                                  p.status === 'overdue' ? 'bg-red-50 text-red-600' :
                                  p.status === 'partially_paid' ? 'bg-amber-50 text-amber-600' :
                                  'bg-zinc-50 text-zinc-500'
                                }`}>
                                <span className={`w-1 h-1 rounded-full shrink-0 ${meta.dot}`} />
                                <span className="truncate">{p.partyName}</span>
                              </div>
                            );
                          })}
                          {dayPayments.length > 2 && (
                            <span className="text-[8px] text-zinc-400 pl-1">+{dayPayments.length - 2} more</span>
                          )}
                        </div>
                      )}

                      {/* Cluster indicator */}
                      {isClustered && !outside && (
                        <div className="absolute bottom-1 right-1 w-2 h-2 rounded-full bg-amber-400" title="Payment cluster" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT PANEL */}
        <div className="w-full lg:w-[340px] shrink-0">
          <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden lg:sticky lg:top-4">

            {rightPanelContent === 'detail' ? (
              <div className="flex flex-col max-h-[calc(100vh-200px)]">
                <div className="px-4 py-3 border-b border-zinc-100 shrink-0">
                  <button onClick={() => setSelectedPaymentId(null)}
                    className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-700 transition-colors mb-2">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M8 2L4 6l4 4" /></svg>
                    Back to day
                  </button>
                  <h2 className="text-base font-semibold text-zinc-900">{selectedPayment.partyName}</h2>
                  <div className="flex items-center gap-2 mt-1">
                    {(() => {
                      const meta = STATUS_META[selectedPayment.status] || STATUS_META.scheduled;
                      return (
                        <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border font-medium ${meta.bg} ${meta.text} ${meta.border}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                          {meta.label}
                        </span>
                      );
                    })()}
                    <span className="text-[11px] text-zinc-400">{selectedPayment.invoiceNumber}</span>
                    {selectedPayment.flag && selectedPayment.flag !== 'none' && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-100">
                        {FLAG_OPTIONS.find(f => f.value === selectedPayment.flag)?.label || selectedPayment.flag}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'Amount', value: fmt(selectedPayment.amount) },
                      { label: 'Outstanding', value: fmt(selectedPayment.outstandingAmount) },
                      { label: 'Due date', value: fmtDateLong(selectedPayment.dueDate) },
                      { label: 'Terms', value: selectedPayment.terms || '-' },
                    ].map(({ label, value }) => (
                      <div key={label} className="bg-zinc-50 rounded-lg p-2.5">
                        <p className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium">{label}</p>
                        <p className="mt-0.5 text-[13px] font-medium text-zinc-900">{value}</p>
                      </div>
                    ))}
                  </div>

                  {(selectedPayment.lineItems || []).length > 0 && !(selectedPayment.lineItems.length === 1 && selectedPayment.lineItems[0].description === 'Transaction total') && (
                    <div>
                      <p className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5">Line items</p>
                      <div className="border border-zinc-100 rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-zinc-50 border-b border-zinc-100">
                              {['Description', 'Qty', 'Amount'].map((h, i) => (
                                <th key={h} className={`px-2.5 py-1.5 text-[9px] uppercase tracking-widest text-zinc-400 font-medium ${i > 0 ? 'text-right' : 'text-left'}`}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedPayment.lineItems || []).map((line, i) => (
                              <tr key={i} className="border-b border-zinc-50 last:border-0">
                                <td className="px-2.5 py-1.5 text-[12px] text-zinc-700">{line.description}</td>
                                <td className="px-2.5 py-1.5 text-[12px] text-zinc-500 text-right tabular-nums">{line.qty}</td>
                                <td className="px-2.5 py-1.5 text-[12px] text-zinc-700 text-right tabular-nums font-medium">{fmt(line.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Note + flag */}
                  <div className="space-y-2">
                    <div>
                      <label className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium block mb-1">Quick note</label>
                      <textarea value={detailNoteDraft} onChange={(e) => setDetailNoteDraft(e.target.value)} rows={2}
                        placeholder="Called vendor, payment delayed..."
                        className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-[12px] text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-900 resize-none" />
                    </div>
                    <div className="flex items-center gap-2">
                      <select value={detailFlagDraft} onChange={(e) => setDetailFlagDraft(e.target.value)}
                        className="flex-1 px-2.5 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-[12px] text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-900">
                        {FLAG_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <button onClick={handleSaveDetails} disabled={!hasUnsavedDetailChanges || isSavingDetails}
                        className="px-4 py-2 bg-zinc-900 text-white text-[10px] uppercase tracking-widest rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-40 shrink-0">
                        {isSavingDetails ? '...' : 'Save'}
                      </button>
                    </div>
                    {detailSaveMessage && <span className="text-[10px] text-emerald-600">{detailSaveMessage}</span>}
                  </div>

                  {/* Conflict history */}
                  {(() => {
                    const conflictsForPayment = conflicts.filter(c => c.paymentId === selectedPayment.id);
                    if (conflictsForPayment.length === 0) return null;
                    return (
                      <div className="border-t border-zinc-100 pt-3">
                        <p className="text-[9px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5">Change history</p>
                        <div className="space-y-1.5">
                          {conflictsForPayment.slice(0, 3).map((c) => (
                            <div key={c.id} className="rounded-lg border border-zinc-100 px-2.5 py-1.5">
                              <p className="text-[12px] text-zinc-700">{c.changes.join(' / ')}</p>
                              <p className="text-[9px] text-zinc-400 mt-0.5">{new Date(c.changedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <>
                <div className={`px-4 py-3 border-b ${
                  selectedDateKey < today && selectedDatePayments.some(p => p.status === 'overdue')
                    ? 'bg-red-50 border-red-100'
                    : selectedDateKey === today ? 'bg-zinc-50 border-zinc-100'
                    : 'border-zinc-100'
                }`}>
                  <p className={`text-base font-semibold ${
                    selectedDateKey < today && selectedDatePayments.some(p => p.status === 'overdue') ? 'text-red-900' : 'text-zinc-900'
                  }`}>
                    {(() => {
                      const d = parseDateKey(selectedDateKey);
                      if (!d) return '';
                      if (selectedDateKey === today) return 'Today';
                      const tomorrow = toDateKey(addDays(new Date(), 1));
                      if (selectedDateKey === tomorrow) return 'Tomorrow';
                      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                    })()}
                  </p>
                  <p className="text-[10px] text-zinc-400 mt-0.5">{fmtDateLong(selectedDateKey)}</p>
                  {selectedDatePayments.length > 0 && (
                    <p className={`text-sm font-medium mt-1.5 tabular-nums ${
                      selectedDatePayments.some(p => p.status === 'overdue') ? 'text-red-600' : 'text-zinc-900'
                    }`}>
                      {fmt(selectedDatePayments.reduce((s, p) => s + p.outstandingAmount, 0))} due
                    </p>
                  )}
                </div>

                <div className="max-h-[calc(100vh-340px)] overflow-y-auto">
                  {selectedDatePayments.length === 0 ? (
                    <div className="py-12 text-center">
                      <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-2">
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-300">
                          <rect x="3" y="4" width="14" height="13" rx="2" /><path d="M7 2v4M13 2v4M3 9h14" />
                        </svg>
                      </div>
                      <p className="text-xs text-zinc-400">No payments due</p>
                    </div>
                  ) : (
                    <div>
                      {selectedDatePayments.map((payment) => {
                        const meta = STATUS_META[payment.status] || STATUS_META.scheduled;
                        return (
                          <button key={payment.id} onClick={() => setSelectedPaymentId(payment.id)}
                            className="w-full text-left px-4 py-3 hover:bg-zinc-50 transition-colors border-b border-zinc-100 last:border-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <span className={`w-2 h-2 rounded-full shrink-0 ${meta.dot}`} />
                                  <p className="text-sm font-medium text-zinc-900 truncate">{payment.partyName}</p>
                                </div>
                                <p className="text-[10px] text-zinc-400 mt-0.5 pl-3.5">{payment.invoiceNumber}</p>
                                {payment.flag && payment.flag !== 'none' && (
                                  <span className="inline-flex items-center gap-1 mt-1 ml-3.5 px-1.5 py-0.5 rounded text-[8px] uppercase tracking-wider bg-amber-50 text-amber-600 border border-amber-100">
                                    {FLAG_OPTIONS.find(f => f.value === payment.flag)?.label || payment.flag}
                                  </span>
                                )}
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-sm font-medium tabular-nums text-zinc-900">{fmt(payment.amount)}</p>
                                {payment.outstandingAmount < payment.amount && (
                                  <p className="text-[10px] text-zinc-400 tabular-nums mt-0.5">{fmt(payment.outstandingAmount)} left</p>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Conflict log (from HK — collapsed) */}
      {conflicts.length > 0 && (
        <details className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <summary className="px-4 py-3 text-[11px] text-zinc-500 cursor-pointer hover:bg-zinc-50 transition-colors select-none">
            External change log · {conflicts.length} update{conflicts.length !== 1 ? 's' : ''}
          </summary>
          <div className="px-4 pb-3 space-y-1.5 max-h-48 overflow-y-auto">
            {conflicts.slice(0, 10).map((c) => (
              <div key={c.id} className="rounded-lg border border-zinc-100 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[12px] font-medium text-zinc-800">{c.invoiceNumber} · {c.partyName}</p>
                  <p className="text-[10px] text-zinc-400 shrink-0">
                    {new Date(c.changedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <p className="text-[11px] text-zinc-500 mt-0.5">{c.changes.join(' · ')}</p>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Notification modal */}
      {isNotificationModalOpen && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-zinc-900/40 backdrop-blur-sm" onClick={() => { setNotificationError(''); setIsNotificationModalOpen(false); }} />
          <div className="relative w-full max-w-md rounded-2xl border border-zinc-200 bg-white shadow-[0_25px_80px_rgba(24,24,27,0.18)] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-zinc-900 flex items-center justify-center">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 6a4 4 0 0 1 8 0c0 4 2 5 2 5H2s2-1 2-5" /><path d="M6.5 13a1.5 1.5 0 0 0 3 0" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-zinc-900">Payment Reminders</p>
                  <p className="text-[12px] text-zinc-400">Step {notificationModalStep} of 2</p>
                </div>
              </div>
              <button onClick={() => { setNotificationError(''); setIsNotificationModalOpen(false); }}
                className="w-7 h-7 rounded-full hover:bg-zinc-100 flex items-center justify-center transition-colors text-zinc-400 hover:text-zinc-600">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M12 4L4 12M4 4l8 8" /></svg>
              </button>
            </div>

            {/* Step indicator */}
            <div className="flex gap-1 px-5 pt-4">
              <div className={`h-1 flex-1 rounded-full transition-colors ${notificationModalStep >= 1 ? 'bg-zinc-900' : 'bg-zinc-200'}`} />
              <div className={`h-1 flex-1 rounded-full transition-colors ${notificationModalStep >= 2 ? 'bg-zinc-900' : 'bg-zinc-200'}`} />
            </div>

            <div className="p-5">
              {/* Step 1: Schedule preferences */}
              {notificationModalStep === 1 && (
                <div className="space-y-5">
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-zinc-400 font-medium mb-3">Digest frequency</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: 'daily', label: 'Daily', desc: 'Every morning' },
                        { value: 'weekly', label: 'Weekly', desc: 'Monday recap' },
                        { value: 'smart', label: 'Smart', desc: 'Only when needed' },
                      ].map((opt) => (
                        <button key={opt.value} onClick={() => setNotificationFrequency(opt.value)}
                          className={`p-3 rounded-xl border text-left transition-all ${notificationFrequency === opt.value
                            ? 'border-zinc-900 bg-zinc-900 text-white shadow-sm'
                            : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300'
                          }`}>
                          <p className="text-[13px] font-medium">{opt.label}</p>
                          <p className={`text-[11px] mt-0.5 ${notificationFrequency === opt.value ? 'text-zinc-400' : 'text-zinc-400'}`}>{opt.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-zinc-400 font-medium mb-3">Remind me before due date</p>
                    <div className="flex flex-wrap gap-2">
                      {[1, 3, 5, 7, 14, 30].map((day) => (
                        <button key={day} onClick={() => toggleLeadDay(day)}
                          className={`px-3 py-1.5 rounded-lg border text-[13px] transition-all ${notificationLeadDays.includes(day)
                            ? 'border-zinc-900 bg-zinc-900 text-white'
                            : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300'
                          }`}>
                          {day === 1 ? '1 day' : `${day} days`}
                        </button>
                      ))}
                    </div>
                    {notificationLeadDays.length === 0 && (
                      <p className="text-[12px] text-amber-500 mt-2">Select at least one reminder window</p>
                    )}
                  </div>

                  <label className="flex items-center justify-between p-3 rounded-xl border border-zinc-200 cursor-pointer hover:border-zinc-300 transition-colors">
                    <div>
                      <p className="text-[13px] text-zinc-800 font-medium">Overdue alerts</p>
                      <p className="text-[12px] text-zinc-400">Get notified when payments are past due</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={notifyOverdue}
                      onClick={() => setNotifyOverdue((v) => !v)}
                      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors ${notifyOverdue ? 'bg-emerald-500 border-emerald-500' : 'bg-zinc-100 border-zinc-200'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${notifyOverdue ? 'translate-x-[17px]' : 'translate-x-[3px]'}`} />
                    </button>
                  </label>
                </div>
              )}

              {/* Step 2: Recipients */}
              {notificationModalStep === 2 && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[11px] uppercase tracking-widest text-zinc-400 font-medium">Recipients</p>
                      <span className="text-[11px] text-zinc-400">{notificationEmails.length}/{MAX_NOTIFICATION_EMAILS}</span>
                    </div>
                    <div className="flex gap-2 mb-3">
                      <input type="email" value={notificationEmailDraft} onChange={(e) => setNotificationEmailDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddNotificationEmail(); } }}
                        placeholder="name@company.com"
                        className="flex-1 px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-[13px] text-zinc-700 placeholder:text-zinc-300 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900" />
                      <button onClick={handleAddNotificationEmail} disabled={notificationEmails.length >= MAX_NOTIFICATION_EMAILS}
                        className="px-4 py-2.5 bg-zinc-900 text-white text-[12px] font-medium rounded-xl hover:bg-zinc-800 transition-colors disabled:opacity-40">
                        Add
                      </button>
                    </div>
                    {notificationEmails.length > 0 && (
                      <div className="space-y-1.5">
                        {notificationEmails.map((email) => (
                          <div key={email} className="flex items-center justify-between px-3 py-2 rounded-lg bg-zinc-50 border border-zinc-100 group">
                            <div className="flex items-center gap-2.5">
                              <div className="w-6 h-6 rounded-full bg-zinc-200 flex items-center justify-center">
                                <span className="text-[10px] font-medium text-zinc-600 uppercase">{email[0]}</span>
                              </div>
                              <span className="text-[13px] text-zinc-700">{email}</span>
                            </div>
                            <button onClick={() => setNotificationEmails((p) => p.filter((e) => e !== email))}
                              className="w-6 h-6 rounded-full flex items-center justify-center text-zinc-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all">
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M12 4L4 12M4 4l8 8" /></svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {notificationEmails.length === 0 && (
                      <div className="text-center py-6 text-zinc-300">
                        <svg width="24" height="24" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" className="mx-auto mb-2">
                          <path d="M4 6a4 4 0 0 1 8 0c0 4 2 5 2 5H2s2-1 2-5" /><path d="M6.5 13a1.5 1.5 0 0 0 3 0" />
                        </svg>
                        <p className="text-[12px]">Add at least one recipient</p>
                      </div>
                    )}
                  </div>

                  {/* Summary */}
                  {notificationEmails.length > 0 && (
                    <div className="p-3 rounded-xl bg-zinc-50 border border-zinc-100">
                      <p className="text-[11px] uppercase tracking-widest text-zinc-400 font-medium mb-1.5">Summary</p>
                      <p className="text-[12px] text-zinc-600 leading-relaxed">
                        {notificationFrequency === 'daily' ? 'Daily digest' : notificationFrequency === 'weekly' ? 'Weekly digest (Mondays)' : 'Smart alerts'} to {notificationEmails.length} {notificationEmails.length === 1 ? 'recipient' : 'recipients'}.
                        Reminders {notificationLeadDays.length > 0
                          ? notificationLeadDays.map((d) => `${d}d`).join(', ') + ' before due.'
                          : 'on due date only.'}
                        {notifyOverdue ? ' Overdue alerts on.' : ''}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {notificationError && <p className="mt-3 text-[12px] text-red-500">{notificationError}</p>}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-5 py-4 border-t border-zinc-100 bg-zinc-50/50">
              <button
                onClick={() => {
                  setNotificationError('');
                  if (notificationModalStep === 1) setIsNotificationModalOpen(false);
                  else setNotificationModalStep(1);
                }}
                className="px-4 py-2 text-[13px] text-zinc-500 hover:text-zinc-700 transition-colors">
                {notificationModalStep === 1 ? 'Cancel' : 'Back'}
              </button>
              {notificationModalStep === 1 ? (
                <button
                  onClick={() => { setNotificationError(''); setNotificationModalStep(2); }}
                  disabled={notificationLeadDays.length === 0}
                  className="px-5 py-2 bg-zinc-900 text-white text-[13px] font-medium rounded-xl hover:bg-zinc-800 transition-colors disabled:opacity-40">
                  Next
                </button>
              ) : (
                <button onClick={handleSaveNotifications} disabled={isSavingNotifications || notificationEmails.length === 0}
                  className="px-5 py-2 bg-zinc-900 text-white text-[13px] font-medium rounded-xl hover:bg-zinc-800 transition-colors disabled:opacity-40">
                  {isSavingNotifications ? 'Saving...' : 'Enable Reminders'}
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default CalendarPro;
